<!-- eslint-disable vue/no-v-html -->
<template>
    <div
        v-bind="!seoExpanded ? { 'data-interface-target': 'expand-description', class: $style.pointer } : {}"
        @click="onExpandTextClick"
    >
        <Typography
            is="div"
            ref="seoRef"
            type="paragraph"
            size="xs"
            :responsive="false"
            :class="[$style.seo, { [$style.seo_expanded]: seoExpanded }, $style[props.color]]"
        >
            <!-- key here helps top re-mount this element and fix glitch on Safari -->
            <SeoTextRaw
                :key="seoExpanded ? 'expanded' : 'collapsed'"
                :content="props.content"
            />

            <SeoVideo
                v-if="props.videoData"
                :videoUrl="props.videoData.videoUrl"
                :posterUrl="props.videoData.posterUrl"
                :videoType="props.videoData.videoType as SeoVideoType"
            />
        </Typography>
    </div>
</template>
<script lang="ts" setup>
import { onMounted, ref } from 'vue'

import SeoVideo, { SeoVideoType } from '@/components_new/SeoVideo.vue'
import Typography from '@/components_new/Typography.vue'

import SeoTextRaw from './SeoTextRaw.vue'

const props = withDefaults(
    defineProps<{
        content: string
        minLinesToShow?: number
        color?: 'white-80' | 'white-60'
        expanded?: boolean
        videoData?: {
            videoUrl: string
            posterUrl?: string
            videoType: SeoVideoType
        }
    }>(),
    { minLinesToShow: 5, color: 'white-60', expanded: false },
)

const seoRef = ref<InstanceType<typeof Typography> | null>(null)
const seoExpanded = ref(props.expanded)
const defaultLineHeight = 16

function onExpandTextClick() {
    if (seoExpanded.value) {
        return
    }
    seoExpanded.value = true
}

onMounted(() => {
    if (!seoRef.value?.$el) {
        return
    }
    const seoClientHeight = seoRef.value.$el.clientHeight
    const seoScrollHeight = seoRef.value.$el.scrollHeight
    let seoLineHeight = parseInt(window.getComputedStyle(seoRef.value.$el).lineHeight, 10)
    if (Number.isNaN(seoLineHeight)) {
        seoLineHeight = defaultLineHeight
    }
    const diff = seoScrollHeight - seoClientHeight
    if (diff / seoLineHeight > props.minLinesToShow) {
        seoExpanded.value = false
    } else {
        seoExpanded.value = true
    }
})
</script>
<style module>
.pointer {
    cursor: pointer;
}
.seo {
    --color: var(--white-60);
    color: var(--color);
    font-size: var(--font-size-paragraph-s);
    line-height: var(--line-height-paragraph-s);
    height: 80px;
    width: 100%;
    overflow: hidden;
    -webkit-mask-image: -webkit-linear-gradient(to bottom, black 55%, transparent 95%);
    mask-image: linear-gradient(to bottom, black 55%, transparent 95%);
}

.seo.white-60 {
    --color: var(--white-60);
}

.seo.white-80 {
    --color: var(--white-80);
}

.seo h2,
.seo h3 {
    margin-top: 24px;
    margin-bottom: 12px;
    color: white;
    font-size: var(--font-size-header-s);
    font-weight: var(--font-weight-accent);
    line-height: var(--line-height-header);
}

.seo h2:first-child {
    margin-top: 0;
}

.seo h4,
.seo h5,
.seo h6 {
    color: white;
    font-size: var(--font-size-paragraph-s);
    line-height: var(--line-height-paragraph-s);
    font-weight: var(--font-weight-accent);
}

.seo ul {
    list-style-type: disc;
    list-style-position: inside;
}
.seo a {
    color: var(--purple-600);
}

.seo a:hover,
.seo a:active {
    color: var(--experimental-salad);
}

@media (--mobile) {
    .seo {
        height: 120px;
    }
}

.seo_expanded {
    height: auto;
    overflow: visible;
    -webkit-mask-image: unset;
    mask-image: unset;
}
</style>
